// easybooking Pauschalen-Assistent: Herbstfest-Pauschale – Werte pflegen, Anleitung fürs Pauschalen-Modul, Live-Prüfung, Landingpage/CTA verdrahten
import React, { useState, useEffect } from "react";
import axios from "axios";
import { WatchChecks } from "./Gerd40Panel";
import { Gift, Loader2, RefreshCw, Save, Copy, CheckCircle2, AlertTriangle, Download, Link2, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/easybooking/packages/herbstfest`;
const eur = (v) => (v === null || v === undefined || v === "" ? "" : String(v).replace(".", ","));

export function PackagePanel({ flash }) {
  const [data, setData] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState("");
  const [check, setCheck] = useState(null);
  const [watch, setWatch] = useState(null);
  const [open, setOpen] = useState(null);
  const load = async () => {
    const [r, w] = await Promise.all([axios.get(API), axios.get(`${API}/watch`)]);
    setData(r.data); setTiers(r.data.spec.tiers); setWatch(w.data);
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { spec, categories, steps, open_points, status, connected } = data;

  const save = async () => {
    setBusy("save");
    try { await axios.put(API, { tiers }); flash("✓ Pauschalen-Werte gespeichert"); await load(); }
    catch (e) { flash(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const runCheck = async () => {
    setBusy("check");
    try { const r = await axios.post(`${API}/check`); setCheck(r.data); flash(r.data.message); await load(); }
    catch (e) { flash(e.response?.data?.detail || "Prüfung fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const apply = async () => {
    setBusy("apply");
    try { const r = await axios.post(`${API}/apply`); flash(`✓ ${r.data.message}`); await load(); }
    catch (e) { flash(e.response?.data?.detail || "Übernahme fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const runWatch = async () => {
    setBusy("watch");
    try {
      const r = await axios.post(`${API}/watch`);
      flash(r.data.status === "ok" ? "✓ Seite & Pauschale sind in Ordnung"
        : r.data.status === "warning" ? `Seite online, ${r.data.warnings.length} Hinweis(e)`
          : `⚠️ ${r.data.problems.length} Problem(e) gefunden${r.data.alert_mail?.sent ? " – Alarm-Mail verschickt" : ""}`);
      await load();
    } catch (e) { flash(e.response?.data?.detail || "Seiten-Prüfung fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const copyStep = (s) => { navigator.clipboard?.writeText(s.fields.map((f) => `${f.label}: ${f.value}`).join("\n")); flash("✓ Werte kopiert"); };  const setTier = (i, patch) => setTiers(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const toggleCat = (i, id) => {
    const t = tiers[i]; const has = t.category_ids.includes(id);
    setTiers(tiers.map((x, j) => j === i ? { ...x, category_ids: has ? x.category_ids.filter((c) => c !== id) : [...x.category_ids, id] }
      : { ...x, category_ids: x.category_ids.filter((c) => c !== id) }));
  };
  const detected = spec.detected_packages || [];

  return (
    <div className="card space-y-3" data-testid="package-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline flex items-center gap-1"><Gift size={12} /> easybooking · Pauschalen-Modul · {spec.name}</div>
        <span className="channel-pill" style={{ background: status === "live" ? "#10B98122" : "#F59E0B22", color: status === "live" ? "#10B981" : "#F59E0B" }} data-testid="package-status">
          {status === "live" ? `${detected.length} Pauschale(n) online in easybooking${spec.audit_status === "deviations" ? " · Abweichungen" : ""}` : "Noch nicht in easybooking angelegt"}
        </span>
      </div>
      <div className="text-xs text-eb-muted">
        Buchungen laufen über die Pauschale im easybooking-Zimmerplan (Leistungen → Pauschalen) – nur dort gelten Aktionspreis und Zeitraum.
        Das Cockpit meldet sich nicht mit deinem Passwort an: Die Werte unten trägst du 1:1 ein; danach erkennt die Prüfung die Pauschale automatisch
        und verdrahtet Landingpage und Newsletter-Button.
      </div>
      {!connected && <div className="text-xs text-rose-400" data-testid="package-not-connected">easybooking-Konto {spec.customer_id} ist nicht verbunden.</div>}
      {open_points.length > 0 && (
        <div className="text-xs text-amber-300 flex items-start gap-1" data-testid="package-open-points"><AlertTriangle size={12} className="mt-0.5" /> {open_points.join(" · ")}</div>
      )}
      {spec.price_list && (
        <div className="text-[11px] text-eb-muted" data-testid="package-price-list">
          Tagespreise laut <a href={spec.price_list.source} target="_blank" rel="noreferrer" className="underline">Preisliste</a> ({spec.price_list.season}, 2 Personen/Nacht):{" "}
          {Object.entries(spec.price_list.per_night_2_persons).map(([k, v]) => `${k} ${v} €`).join(" · ")} · je weitere Person +{spec.price_list.extra_person_per_night} €/Nacht
        </div>
      )}

      <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="package-pricing-mode">
        <span className="text-eb-muted">Zusatzpersonen:</span>
        <select value={spec.pricing_mode || "extra_person"} onChange={async (e) => { await axios.put(API, { pricing_mode: e.target.value }); flash("✓ Preismodell gespeichert"); await load(); }}
          className="!w-auto" data-testid="package-pricing-mode-select">
          <option value="two_persons">Nur für 2 Personen (ab 3 Gästen Normaltarif)</option>
          <option value="extra_person">Zusatzperson 39/49 € (2–5 Gäste, Preise je Belegung in easybooking eintragen)</option>
          <option value="per_group_package">Eigene Pauschale je Personenzahl (2/3/4/5 Gäste, je ein Festpreis – easybooking-Empfehlung)</option>
        </select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="package-tiers">
        {tiers.map((t, i) => (
          <div key={t.key} className="eb-surface rounded-md p-3 space-y-2" data-testid={`package-tier-${t.key}`}>
            <input value={t.label} onChange={(e) => setTier(i, { label: e.target.value })} className="font-semibold text-sm" data-testid={`tier-label-${t.key}`} />
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label>2 Nächte · {t.base_persons} Gäste (€)<input value={eur(t.base_price)} onChange={(e) => setTier(i, { base_price: e.target.value })} data-testid={`tier-price2-${t.key}`} /></label>
              <label>2 Nächte · je weitere Person (€)<input value={eur(t.extra_person_price)} onChange={(e) => setTier(i, { extra_person_price: e.target.value })} data-testid={`tier-extra2-${t.key}`} /></label>
              <label>1 Nacht · {t.base_persons} Gäste (€)<input placeholder="noch festlegen" value={eur(t.price_1n)} onChange={(e) => setTier(i, { price_1n: e.target.value })} data-testid={`tier-price1-${t.key}`} /></label>
              <label>1 Nacht · je weitere Person (€)<input placeholder="noch festlegen" value={eur(t.extra_1n)} onChange={(e) => setTier(i, { extra_1n: e.target.value })} data-testid={`tier-extra1-${t.key}`} /></label>
            </div>
            <div className="text-[10px] text-eb-muted">Kategorien (easybooking):</div>
            <div className="flex flex-wrap gap-1">
              {categories.map((c) => {
                const on = t.category_ids.includes(c.id);
                return (
                  <button key={c.id} onClick={() => toggleCat(i, c.id)} className="channel-pill" data-testid={`tier-cat-${t.key}-${c.id}`}
                    style={{ background: on ? "#E58A1F33" : "#374151", color: on ? "#F5B463" : "#9CA3AF", cursor: "pointer" }}>{c.name} · {c.rooms}</button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn" onClick={save} disabled={!!busy} data-testid="package-save">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Werte speichern</button>
        <button className="btn btn-primary" onClick={runCheck} disabled={!!busy || !connected} data-testid="package-check">{busy === "check" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Jetzt in easybooking prüfen</button>
        <button className="btn" onClick={apply} disabled={!!busy || !connected} data-testid="package-apply">{busy === "apply" ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Landingpage & Newsletter-Button verdrahten</button>
        <a className="btn" href={`${API}/divi`} data-testid="package-divi-download"><Download size={14} /> Divi-Code herunterladen</a>
        <button className="btn" onClick={runWatch} disabled={!!busy} data-testid="package-watch-run">{busy === "watch" ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Seite jetzt prüfen</button>
        {spec.checked_at && <span className="text-[10px] text-eb-muted">Zuletzt geprüft: {new Date(spec.checked_at).toLocaleString("de-DE")}</span>}
      </div>

      <div className="eb-surface rounded-md p-3 space-y-1" data-testid="package-watch">
        <div className="text-xs font-semibold flex items-center gap-1">
          {!watch?.last ? <ShieldCheck size={12} className="text-eb-muted" />
            : watch.last.status === "ok" ? <CheckCircle2 size={12} className="text-emerald-400" />
              : watch.last.status === "warning" ? <AlertTriangle size={12} className="text-amber-300" />
                : <AlertTriangle size={12} className="text-rose-400" />}
          Seiten-Wächter · alle 15 Minuten
          <span className="text-eb-muted font-normal">
            {!watch?.last ? "· noch keine Prüfung gelaufen"
              : watch.last.status === "ok" ? "· Seite, Pauschale, Bilder, Buchungsfenster und Verfügbarkeit in Ordnung · Prüfung alle 15 Minuten"
                : watch.last.status === "warning" ? `· ${watch.last.warnings.length + watch.last.checks.filter((c) => c.hint && !c.ok && c.group !== "Doppelte Blöcke").length} Hinweis(e)` : `· ${watch.last.problems.length} Problem(e)`}
          </span>
        </div>
        <div className="text-[11px] text-eb-muted break-all">
          Geprüfte Seite: {watch?.url} · Alarm-Mail an {watch?.alert_email}
          {watch?.last && <> · letzte Prüfung {new Date(watch.last.at).toLocaleString("de-DE")} ({watch.last.source === "cron" ? "automatisch" : "manuell"})</>}
        </div>
        {watch?.last?.problems?.map((p, i) => (
          <div key={i} className="text-xs text-rose-400" data-testid={`package-watch-problem-${i}`}>• {p}</div>
        ))}
        {watch?.last?.warnings?.map((p, i) => (
          <div key={i} className="text-xs text-amber-300" data-testid={`package-watch-warning-${i}`}>• {p}</div>
        ))}
        {watch?.last?.checks?.length > 0 && <WatchChecks checks={watch.last.checks} prefix="package-watch" />}
        {watch?.last?.page?.found?.length > 0 && (
          <div className="text-[11px] text-emerald-400" data-testid="package-watch-found">Gefunden: {watch.last.page.found.join(" · ")}</div>
        )}
        {watch?.last?.alert_mail?.error && (
          <div className="text-[11px] text-amber-300" data-testid="package-watch-mail-error">Alarm-Mail nicht verschickt: {watch.last.alert_mail.error}</div>
        )}
        {watch?.last?.alert_mail?.sent && (
          <div className="text-[11px] text-emerald-400" data-testid="package-watch-mail-sent">Alarm-Mail an {watch.last.alert_mail.to} verschickt</div>
        )}
        {watch?.last?.dup_mail?.sent && (
          <div className="text-[11px] text-amber-300" data-testid="package-watch-dup-mail-sent">Mail zum doppelten Block an {watch.last.dup_mail.to} verschickt</div>
        )}
        {watch?.last?.dup_mail?.error && (
          <div className="text-[11px] text-amber-300" data-testid="package-watch-dup-mail-error">Mail zum doppelten Block nicht verschickt: {watch.last.dup_mail.error}</div>
        )}
      </div>

      {(check?.all_packages?.length > 0 || detected.length > 0) && (
        <div className="eb-surface rounded-md p-3 space-y-1" data-testid="package-detected">
          <div className="text-xs font-semibold">In easybooking gefunden:</div>
          {(check?.all_packages || detected).map((p) => (
            <div key={p.id} className="text-xs flex items-center gap-2 flex-wrap" data-testid={`package-found-${p.id}`}>
              {p.herbstfest ? <CheckCircle2 size={12} className="text-emerald-400" /> : <AlertTriangle size={12} className="text-amber-400" />}
              <span className="font-mono text-eb-muted">#{p.id}</span><span className="font-medium">{p.title}</span><span className="text-eb-muted">{p.dates} · {p.price}</span>
            </div>
          ))}
        </div>
      )}
      {spec.cta_url && <div className="text-[10px] text-eb-muted break-all" data-testid="package-cta">Newsletter-Button → {spec.cta_url}</div>}

      {spec.audit_status && spec.audit_status !== "pending" && (
        <div className="eb-surface rounded-md p-3 space-y-2" data-testid="package-audit">
          <div className="text-xs font-semibold flex items-center gap-1">
            {spec.audit_status === "ok" ? <CheckCircle2 size={12} className="text-emerald-400" /> : <AlertTriangle size={12} className="text-rose-400" />}
            Soll/Ist-Abgleich mit easybooking: {spec.audit_status === "ok" ? "alles stimmt" : `${spec.audit.filter((a) => a.severity === "error").length} Abweichung(en), ${spec.audit.filter((a) => a.severity === "warning").length} Hinweis(e)`}
            {spec.audit_status === "ok" && spec.audit.length > 0 && <span className="text-eb-muted font-normal">· {spec.audit.length} Hinweis(e)</span>}
          </div>
          {spec.audit.map((a, i) => (
            <div key={i} className="text-xs grid grid-cols-1 md:grid-cols-[180px_1fr_1fr_1fr] gap-x-3 border-t border-white/5 pt-1" data-testid={`package-audit-${i}`}>
              <div className={a.severity === "error" ? "text-rose-400 font-medium" : a.severity === "info" ? "text-eb-muted font-medium" : "text-amber-300 font-medium"}>{a.field}{a.offer_id ? <span className="text-eb-muted font-mono"> · #{a.offer_id}</span> : ""}</div>
              <div><span className="text-eb-muted">Ist:</span> {a.ist}</div>
              <div><span className="text-eb-muted">Soll:</span> {a.soll}</div>
              <div><span className="text-eb-muted">Tun:</span> {a.fix}</div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1" data-testid="package-steps">
        <div className="overline">Anleitung fürs easybooking Pauschalen-Modul ({steps.length} Schritte)</div>
        {steps.map((s, i) => (
          <div key={i} className="eb-surface rounded-md" data-testid={`package-step-${i}`}>
            <button className="w-full flex items-center justify-between px-3 py-2 text-left text-xs" onClick={() => setOpen(open === i ? null : i)} data-testid={`package-step-toggle-${i}`}>
              <span className="font-medium">{i + 1}. {s.title}{s.fields.some((f) => f.missing) && <span className="text-amber-400 ml-2">· Preis fehlt</span>}</span>
              {open === i ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {open === i && (
              <div className="px-3 pb-3 space-y-1 text-xs">
                <p className="text-eb-muted">{s.text}</p>
                <table className="w-full"><tbody>
                  {s.fields.map((f) => (
                    <tr key={f.label} className="border-t border-white/5"><td className="py-1 pr-2 text-eb-muted whitespace-nowrap align-top">{f.label}</td>
                      <td className={`py-1 ${f.missing ? "text-amber-400 font-semibold" : ""}`}>{f.value}</td></tr>
                  ))}
                </tbody></table>
                <button className="btn !py-1" onClick={() => copyStep(s)} data-testid={`package-step-copy-${i}`}><Copy size={12} /> Werte kopieren</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
